// src/components/auth/ProtectedRoute.tsx - Updated with onboarding logic
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useAppStore } from '@/stores/appStore';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredTier?: 'free' | 'pro' | 'enterprise';
  requiredFeature?: string;
  fallbackUrl?: string;
}

const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
        Loading...
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        Verifying your authentication
      </p>
    </div>
  </div>
);

const SubscriptionGate: React.FC<{
  requiredTier: 'pro' | 'enterprise';
  children: React.ReactNode;
}> = ({ requiredTier, children }) => {
  const { subscriptionStatus } = useAppStore();
  
  if (!subscriptionStatus) {
    return <Navigate to="/subscription" replace />;
  }

  const currentTier = subscriptionStatus.subscription.tier;
  const tierLevels = { free: 0, pro: 1, enterprise: 2 };
  
  if (tierLevels[currentTier] < tierLevels[requiredTier]) {
    return <Navigate to="/upgrade" replace />;
  }

  return <>{children}</>;
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredTier,
  requiredFeature,
  fallbackUrl = '/auth/signin'
}) => {
  const location = useLocation();
  const { 
    isAuthenticated, 
    isLoading, 
    checkAuthState, 
    userProfile,
    shouldRedirectToOnboarding // ADDED: Onboarding helper
  } = useAuthStore();
  
  const { 
    subscriptionStatus, 
    fetchSubscriptionStatus 
  } = useAppStore();

  useEffect(() => {
    // Check auth state on mount if not already authenticated
    if (!isAuthenticated && !isLoading) {
      checkAuthState();
    }
  }, [isAuthenticated, isLoading, checkAuthState]);

  useEffect(() => {
    // Fetch subscription status if authenticated but not loaded
    if (isAuthenticated && !subscriptionStatus) {
      fetchSubscriptionStatus();
    }
  }, [isAuthenticated, subscriptionStatus, fetchSubscriptionStatus]);

  // Show loading while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to={fallbackUrl} 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // UPDATED: Check if user profile exists first
  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // ADDED: Check if user needs onboarding (except for onboarding page itself)
  const isOnboardingPage = location.pathname === '/onboarding';
  
  if (!isOnboardingPage && shouldRedirectToOnboarding()) {
    console.log('User needs onboarding, redirecting from:', location.pathname);
    return <Navigate to="/onboarding" replace />;
  }

  // ADDED: If user completed onboarding but is still on onboarding page, redirect to dashboard
  if (isOnboardingPage && userProfile.onboarding_completed) {
    console.log('User completed onboarding, redirecting to dashboard...');
    return <Navigate to="/" replace />;
  }

  // Check subscription requirements
  if (requiredTier && requiredTier !== 'free') {
    return (
      <SubscriptionGate requiredTier={requiredTier}>
        {children}
      </SubscriptionGate>
    );
  }

  // Check feature-specific requirements
  if (requiredFeature && subscriptionStatus) {
    const hasFeature = checkFeatureAccess(subscriptionStatus, requiredFeature);
    if (!hasFeature) {
      return <Navigate to="/upgrade" replace />;
    }
  }

  return <>{children}</>;
};

// Helper function to check feature access
function checkFeatureAccess(
  subscriptionStatus: any,
  feature: string
): boolean {
  const { subscription, limits } = subscriptionStatus;

  switch (feature) {
    case 'auto_scheduling':
      return subscription.auto_schedule_enabled;
    
    case 'multiple_channels':
      return limits.channels_limit > 1;
    
    case 'unlimited_videos':
      return subscription.tier === 'enterprise';
    
    case 'analytics':
      return ['pro', 'enterprise'].includes(subscription.tier);
    
    case 'priority_support':
      return subscription.tier === 'enterprise';
    
    case 'api_access':
      return ['pro', 'enterprise'].includes(subscription.tier);
    
    default:
      return true;
  }
}

// Higher-order component for easy usage
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<ProtectedRouteProps, 'children'>
) => {
  return (props: P) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

// UPDATED: Hook for checking feature access and onboarding status in components
export const useFeatureAccess = () => {
  const { subscriptionStatus } = useAppStore();
  const { userProfile, shouldRedirectToOnboarding } = useAuthStore();

  return {
    hasFeature: (feature: string) => 
      subscriptionStatus ? checkFeatureAccess(subscriptionStatus, feature) : false,
    canCreateChannel: subscriptionStatus?.can_create_channel || false,
    canCreateVideo: subscriptionStatus?.can_create_video || false,
    subscription: subscriptionStatus?.subscription,
    usage: subscriptionStatus?.usage,
    limits: subscriptionStatus?.limits,
    // ADDED: Onboarding helpers
    needsOnboarding: shouldRedirectToOnboarding(),
    onboardingCompleted: userProfile?.onboarding_completed || false,
    userProfile,
  };
};

export default ProtectedRoute;